import { Language } from "@/constants";
import { URLS } from "../urls";

type BuildSyncRequest = {
  company_name: string;
  full_company_name?: string;
  company_description?: string;
  language?: string;
  max_mentions?: number;
};

type BuildSyncSearchQuery = {
  keywords: string[];
  near_keywords: string[];
  not_near_keywords: string[];
  near_distance: number;
  not_near_distance: number;
  near_near_keywords: string[];
  languages: Language[];
  ambiguity_tier: string;
};

type BuildSyncQueryStructureUsed = {
  search_term: string;
  languages: Language[];
  near_keywords: string[];
  near_distance: number;
  not_near_keywords: string[];
  not_near_distance: number;
  page_size: number;
};

type BuildSyncMentionRaw = {
  mention: {
    context: string;
    text: string;
    published_date: string;
    url_with_timing: string;
    timing: number;
    display_text: string | null;
    indices_metadata: unknown | null;
  };
  channel: {
    name: string;
    channel_type: string;
    artwork_url: string;
    language: string;
    is_external_source: boolean;
    external_source_url: string;
    current_reach: number;
    website_url: string;
  };
  episode: {
    title: string;
    description: string;
    published_date: string;
    ingestion_metadata: unknown | null;
    ingestion_transcript: unknown | null;
    artwork_url: string;
    length_seconds: number;
    public_url: string | null;
    external_media_source: string;
    reach: number;
    reach_source_type: string | null;
    reach_description: string | null;
    reach_date: string | null;
    id: string | null;
    audio_url: string | null;
    guid: string | null;
    youtube_id: string | null;
    has_transcript: boolean | null;
    transcript_status: string | null;
    channel_artwork_url: string | null;
    episode_artwork_url: string | null;
  };
  pr_value: number;
  pr_value_currency: string;
};

type BuildSyncMention = {
  text: string;
  context: string;
  published_date: string;
  url: string;
  timing: number;
  channel_name: string;
  channel_type: string;
  channel_language: string;
  channel_reach: number;
  episode_title: string;
  episode_id: string | null;
  episode_published_date: string;
  episode_public_url: string | null;
  episode_reach: number;
  pr_value: number;
  pr_value_currency: string;
  raw: BuildSyncMentionRaw;
};

type BuildSyncTiming = {
  step: string;
  seconds: number;
  detail: Record<string, unknown> | null;
};

type BuildSyncResponse = {
  company_name: string;
  full_company_name: string;
  company_description: string;
  language: string;
  query_structure: {
    search_query: BuildSyncSearchQuery;
    disambiguation: {
      question: string;
    };
  };
  query_structure_used: BuildSyncQueryStructureUsed;
  disambiguation_question: string;
  ambiguity_tier: string;
  relaxed_level: string;
  mentions_fetched: number;
  mentions_before_judge: BuildSyncMention[];
  judge_skipped: boolean;
  mentions_relevant: number;
  mentions: BuildSyncMention[];
  oldest_mention_date: string;
  oldest_relevant_mention_date: string;
  precision: number;
  coverage_assessment: string;
  quality_warning: string | null;
  timings: BuildSyncTiming[];
};

export const buildSync = async (
  request: BuildSyncRequest
): Promise<BuildSyncResponse> => {
  const response = await fetch(URLS.BUILD_SYNC, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    throw new Error(`Build sync request failed with status ${response.status}`);
  }

  return response.json() as Promise<BuildSyncResponse>;
};
